/**
 * Kanban data transformation utilities
 * These functions optimize the process of transforming raw ticket and tag data
 * into the format required by the Kanban board components
 */

import { i18n } from "../translate/i18n";

/**
 * Efficiently builds the lane structure from tickets and tags
 * Uses memoization at the data level to avoid unnecessary object creation
 */
export const buildKanbanLanes = (tickets, tags) => {
  if (!tickets || !Array.isArray(tickets)) {
    return [];
  }

  try {
    // Build a tag index for faster lookups
    const tagIndex = tags.reduce((acc, tag) => {
      acc[tag.id] = tag;
      return acc;
    }, {});

    const lanes = [];

    // Create default lane for untagged tickets
    const untaggedTickets = tickets.filter(ticket => !ticket.tags || ticket.tags.length === 0);
    lanes.push({
      id: "lane0",
      title: i18n.t("tagsKanban.laneDefault"),
      cards: untaggedTickets,
      backgroundColor: null,
      cardCount: untaggedTickets.length,
    });

    // Create lanes for each tag
    if (tagIndex && Object.keys(tagIndex).length > 0) {
      Object.values(tagIndex).forEach(tag => {
        const laneTickets = tickets.filter(ticket =>
          ticket.tags && ticket.tags.some(t => t.id === tag.id)
        );

        lanes.push({
          id: tag.id.toString(),
          title: tag.name,
          cards: laneTickets,
          backgroundColor: tag.color,
          cardCount: laneTickets.length,
        });
      });
    }

    return lanes;
  } catch (error) {
    console.error("Error building kanban lanes:", error);
    return [];
  }
};

/**
 * Utility to check if lanes data has meaningfully changed
 * Used for memoization decisions
 */
export const hasLanesChanged = (prevLanes, nextLanes) => {
  if (prevLanes.length !== nextLanes.length) return true;

  return prevLanes.some((lane, idx) => {
    const nextLane = nextLanes[idx];
    if (!nextLane) return true;
    if (lane.id !== nextLane.id) return true;
    if (lane.cardCount !== nextLane.cardCount) return true;
    return false;
  });
};

/**
 * Filter tickets by date range efficiently
 */
export const filterTicketsByDateRange = (tickets, startDate, endDate) => {
  if (!tickets || !Array.isArray(tickets)) {
    return [];
  }

  const start = new Date(startDate);
  const end = new Date(endDate);
  end.setHours(23, 59, 59, 999); // Include the entire end date

  return tickets.filter(ticket => {
    try {
      const ticketDate = new Date(ticket.updatedAt);
      return ticketDate >= start && ticketDate <= end;
    } catch (error) {
      console.error("Error filtering ticket date:", ticket.id);
      return false;
    }
  });
};

/**
 * Batch update multiple tickets with new tag assignment
 * Used for optimizing move operations
 */
export const updateTicketTag = (ticket, newTagId, removeTagId) => {
  const updatedTicket = { ...ticket };

  // Remove old tag if specified
  if (removeTagId) {
    updatedTicket.tags = updatedTicket.tags.filter(tag => tag.id !== removeTagId);
  }

  // Add new tag if specified
  if (newTagId && newTagId !== "lane0") { // lane0 is default, no tag needed
    const newTag = { id: newTagId };
    if (!updatedTicket.tags.some(tag => tag.id === newTagId)) {
      updatedTicket.tags = [...updatedTicket.tags, newTag];
    }
  }

  return updatedTicket;
};
